import React from 'react';
import { Box, Paper, Stack, SxProps, Theme, Typography } from '@mui/material';

/**
 * A titled, outlined card — the building block of detail pages and drawers.
 * `action` sits on the title row (e.g. "View all", "Edit").
 */
export const SectionCard: React.FC<{
  title?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ title, action, children, sx }) => (
  <Paper variant="outlined" sx={[{ borderRadius: 2.5, px: 2.5, py: 2 }, ...(Array.isArray(sx) ? sx : [sx])]}>
    {(title || action) && (
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5, minHeight: 24 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {title}
        </Typography>
        {action}
      </Stack>
    )}
    {children}
  </Paper>
);

/** A label over its value; shows "Not provided" in muted text when empty. */
export const InfoField: React.FC<{
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
  sx?: SxProps<Theme>;
}> = ({ label, value, mono, sx }) => {
  const empty = value === undefined || value === null || value === '';
  return (
    <Box sx={[{ minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography
        component="div"
        variant="body2"
        fontWeight={empty ? 400 : 500}
        color={empty ? 'text.disabled' : 'text.primary'}
        sx={{ wordBreak: 'break-word', fontFamily: mono && !empty ? 'monospace' : undefined }}
      >
        {empty ? 'Not provided' : value}
      </Typography>
    </Box>
  );
};

/** Two-column grid of InfoFields (one column on phones). */
export const InfoGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 1.75 }}>
    {children}
  </Box>
);
